import _Vue from "vue";
import responsiveModule from "./store/responsive";

import { ResponsivePluginsOptions, MediaQuery, MediaQueriesList } from "./Responsive.interfaces";

export default {
    install: (Vue: typeof _Vue, options: ResponsivePluginsOptions): void => {

        if (!options || !options.store) {
            throw new Error("Please initialise plugin with a Vuex store.");
        }

        // Register modals vuex module
        options.store.registerModule("responsiveModule", responsiveModule);

        // Breakpoints List
        const breakpoints = {
            "x-small": 0,
            "small": 36,
            "medium": 48,
            "desktop": 62,
            "widescreen": 75
        };

        const containerWidth = {
            "x-small": undefined,
            "small": 540,
            "medium": 720,
            "desktop": 960,
            "widescreen": 1140
        };

        // Media Queries List
        // 0.0625em is equal to 1px, and is use to make sure the "smaller" breakpoint doesn't overide with the next (e.g. small stop at 767px and not 768px)
        const mediaQueries = {
            "x-small": "screen and (min-width: " + breakpoints["x-small"] + "em) and (max-width: " + (breakpoints.small - 0.0625) +"em)",
            "small": "screen and (min-width:" + breakpoints.small + "em) and (max-width: " + (breakpoints.medium - 0.0625) +"em)",
            "medium": "screen and (min-width:" + breakpoints.medium + "em) and (max-width: " + (breakpoints.desktop - 0.0625) +"em)",
            "desktop": "screen and (min-width:" + breakpoints.desktop + "em) and (max-width: " + (breakpoints.widescreen - 0.0625) +"em)",
            "widescreen": "screen and (min-width:" + breakpoints.widescreen + "em)",
            "from-small": "screen and (min-width:" + breakpoints.small + "em)",
            "from-medium": "screen and (min-width:" + breakpoints.medium + "em)",
            "from-desktop": "screen and (min-width:" + breakpoints.desktop + "em)",
            "from-widescreen": "screen and (min-width:" + breakpoints.widescreen + "em)",
            "to-small": "screen and (max-width:" + (breakpoints.small - 0.0625) + "em)",
            "to-medium": "screen and (max-width:" + (breakpoints.medium - 0.0625) + "em)",
            "to-desktop": "screen and (max-width:" + (breakpoints.desktop - 0.0625) + "em)",
            "to-widescreen": "screen and (max-width:" + (breakpoints.widescreen - 0.0625) + "em)"
        } as MediaQueriesList;

        const mediaQueriesList = [] as Array<MediaQuery>;

        /**
         * [mediaQuery Create new mediaQueryList with their callbacks and register it to mediaQueriesList array]
         * @param  {string} breakpoint      [A string representing the media query]
         * @param  {function} matchCallback   [The callback launch if the mediaQuery matche]
         * @param  {function} unmatchCallback [The cllback launch if the mediaQuery doesn't matche]
         * @return {int}                 [The index of the new entry in mediaQueriesList array]
         */
        function mediaQuery (breakpoint: string, matchCallback: () => void, unmatchCallback?: () => void) {
            // Check Parameters
            // ---------------------
            // breakpoint
            if(breakpoint === undefined) {
                throw new Error("breakpoint is required, got undefined");
            }

            const breakpointType = typeof breakpoint;
            if(breakpointType !== "string") {
                throw new Error(`breakpoint: string expected, got ${breakpointType}`);
            }

            if(!mediaQueries.hasOwnProperty(breakpoint)) {
                throw new Error(`breakpoint: ${breakpoint} is not a valid breakpoint, should be one of the following: ${mediaQueries}`);
            }

            // matchCallback
            if(matchCallback === undefined) {
                throw new Error("matchCallback is required, got undefined");
            }

            const matchCallbackType = typeof matchCallback;
            if(matchCallbackType !== "function") {
                throw new Error(`matchCallback: function expected, got ${matchCallbackType}`);
            }

            // unmatchCallback
            const unmatchCallbackType = typeof unmatchCallback;

            if(unmatchCallback !== undefined && unmatchCallbackType !== "function") {
                throw new Error(`unmatchCallback: function expected, got ${unmatchCallbackType}`);
            }

            // Build Media Query
            // ---------------------
            const matchMedia: MediaQueryList = window.matchMedia(mediaQueries[breakpoint]);

            const screenTest = (e: MediaQueryListEvent): void => {
                if (e.matches) {
                    matchCallback();
                } else {
                    if(unmatchCallback !== undefined) {
                        unmatchCallback();
                    }
                }
            };

            if (matchMedia.matches) {
                matchCallback();
            }

            // Some browsers should use addEventListener when other browser (IE 11 / Edge and, probably older safari are still using addListener)
            if (matchMedia.addEventListener) {
                matchMedia.addEventListener("change", screenTest);
            } else {
                matchMedia.addListener(screenTest);
            }

            mediaQueriesList.push({
                "matchMedia": matchMedia,
                "screenTest": screenTest
            });

            const index = mediaQueriesList.length - 1;

            return index;
        }

        /**
         * [removeMediaQuery Remove listener on a given mediaQuery entry in mediaQueriesList]
         * @param  {number} mediaQueryIndex [The index of the entry in mediaQueriesList]
s         */
        function removeMediaQuery (mediaQueryIndex: number) {
            if(mediaQueriesList[mediaQueryIndex]) {

                const matchMedia = mediaQueriesList[mediaQueryIndex].matchMedia;
                const screenTest = mediaQueriesList[mediaQueryIndex].screenTest;

                // Some browsers should use addEventListener when other browser (IE 11 / Edge and, probably older safari are still using addListener)
                if (matchMedia.addEventListener) {
                    matchMedia.addEventListener("change", screenTest);
                } else {
                    matchMedia.addListener(screenTest);
                }
            }

            return false;
        }

        // Set Current Breakpoint
        // ---------------------
        // Widescreen
        mediaQuery("widescreen", () => {
            options.store.commit("responsiveModule/setCurrentBreakpoint", "widescreen");
        });

        // Desktop
        mediaQuery("desktop", () => {
            options.store.commit("responsiveModule/setCurrentBreakpoint", "desktop");
        });

        // Medium
        mediaQuery("medium", () => {
            options.store.commit("responsiveModule/setCurrentBreakpoint", "medium");
        });

        // Small
        mediaQuery("small", () => {
            options.store.commit("responsiveModule/setCurrentBreakpoint", "small");
        });

        // X-small
        mediaQuery("x-small", () => {
            options.store.commit("responsiveModule/setCurrentBreakpoint", "x-small");
        });

        // Save the current window.width and register global resize event listener
        options.store.commit("responsiveModule/setCurrentWindowWidth", window.innerWidth);
        window.addEventListener("resize", () => {
            options.store.commit("responsiveModule/setCurrentWindowWidth", window.innerWidth);
        });

        Vue.prototype.$responsive = {
            breakpoints: function() {
                return breakpoints;
            },
            containerWidth: function(breakpoint: any) {
                let containerWidthValue = undefined;
                switch(breakpoint) {
                case "xsmall":
                    containerWidthValue = containerWidth["x-small"];
                    break;
                case "small":
                    containerWidthValue = containerWidth["small"];
                    break;
                case "medium":
                    containerWidthValue = containerWidth["medium"];
                    break;
                case "desktop":
                    containerWidthValue = containerWidth["desktop"];
                    break;
                case "widescreen":
                    containerWidthValue = containerWidth["widescreen"];
                    break;
                }
                return containerWidthValue;
            },
            registerMediaQuery: function(breakpoint: string, matchCallback: () => void, unmatchCallback?: () => void) {
                return mediaQuery(breakpoint, matchCallback, unmatchCallback);
            },
            unregisterMediaQuery: function(mediaQueryIndex: number) {
                return removeMediaQuery(mediaQueryIndex);
            },
        };
    }
};
